import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../store';
import {
	ConsumerInputNeededState,
	isProcessingSessionState,
	isSessionConsumerInputNeededState,
	Session,
	SessionState
} from '../../types/session';
import {
	initSessionStarted,
	initSessionSuccess,
	startSessionStarted,
	startSessionSuccess
} from '../actions/session.actions';
import { chooseBank } from '../thunks/bank-selection.thunk';

export type IdleState = { state: 'IDLE' };
export type CreatingState = { state: 'CREATING' };
export type CreatedState = { state: 'CREATED' } & Session;
export type StartingState = { state: 'STARTING' } & Session;
export type StartedState = { state: 'STARTED' } & Session & StartedStateStatus;

export type StartedStateStatus =
	| {
			status: Exclude<SessionState['status'], 'CONSUMER_INPUT_NEEDED'>;
	  }
	| {
			status: 'CONSUMER_INPUT_NEEDED';
			action: ConsumerInputNeededState['result']['type'];
			polling?: boolean;
	  };

export type State = IdleState | CreatingState | CreatedState | StartingState | StartedState;

const initialState: State = {
	state: 'IDLE'
};

export const sessionSlice = createSlice({
	name: 'session',
	initialState: initialState as State,
	reducers: {
		changeSessionState(state, action: PayloadAction<SessionState>) {
			if (!isStartedState(state)) return state;

			const nextSessionState = action.payload;
			if (isSessionConsumerInputNeededState(nextSessionState)) {
				const status = nextSessionState.status;
				const action = nextSessionState.result.type;
				const polling = nextSessionState.polling;
				return {
					...state,
					polling,
					status,
					action
				};
			}

			if (isProcessingSessionState(nextSessionState)) {
				return {
					...state,
					status: 'PROCESSING',
					polling: nextSessionState.polling
				};
			}

			const status = nextSessionState.status;
			return {
				...state,
				status
			};
		}
	},
	extraReducers: (builder) => {
		builder.addCase(initSessionStarted, (state) => {
			if (!isIdleState(state)) return state;

			return {
				state: 'CREATING'
			};
		});

		builder.addCase(initSessionSuccess, (state, action) => {
			if (!isCreatingState(state)) return state;

			const session = action.payload;
			return {
				state: 'CREATED',
				...session
			};
		});

		builder.addCase(startSessionStarted, (state, action) => {
			if (!isCreatedState(state)) return state;

			return {
				...state,
				state: 'STARTING'
			};
		});

		builder.addCase(startSessionSuccess, (state, action: PayloadAction<SessionState>) => {
			if (!isStartingState(state)) return state;

			const sessionState = action.payload;
			if (isSessionConsumerInputNeededState(sessionState)) {
				const status = sessionState.status;
				const action = sessionState.result.type;
				const polling = sessionState.polling;
				return {
					...state,
					state: 'STARTED',
					polling,
					status,
					action
				};
			}

			if (isProcessingSessionState(sessionState)) {
				return {
					...state,
					status: 'PROCESSING',
					polling: sessionState.polling
				};
			}

			const status = sessionState.status;
			return {
				...state,
				state: 'STARTED',
				status
			};
		});

		builder.addCase(chooseBank.fulfilled, (state, action) => {
			return sessionSlice.caseReducers.changeSessionState(state, {
				type: changeSessionState.type,
				payload: action.payload.nextSessionState
			});
		});
	}
});

export const { changeSessionState } = sessionSlice.actions;

const isIdleState = (state: State): state is IdleState => {
	return state.state === 'IDLE';
};

const isCreatingState = (state: State): state is CreatingState => {
	return state.state === 'CREATING';
};

const isCreatedState = (state: State): state is CreatedState => {
	return state.state === 'CREATED';
};

const isStartingState = (state: State): state is StartingState => {
	return state.state === 'STARTING';
};

const isStartedState = (state: State): state is StartedState => {
	return state.state === 'STARTED';
};

// SELECTORS

export const selectSessionState = (state: RootState) => state.session.state;

export const selectSessionStatus = (state: RootState) => {
	const session = state.session;
	if (session.state !== 'STARTED') return undefined;
	return session.status;
};

export const selectSessionId = (state: RootState) => {
	const session = state.session;
	if (session.state !== 'STARTED') return undefined;

	return session.sessionId;
};

export const selectConsumerInputNeededAction = (state: RootState) => {
	const session = state.session;
	if (session.state !== 'STARTED' || session.status !== 'CONSUMER_INPUT_NEEDED') return undefined;

	return session.action;
};

export const selectCurrentSessionState = (state: RootState): StartedState | undefined => {
	if (state.session.state !== 'STARTED') return undefined;
	return state.session;
};
